import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import type { EngagementPeopleRow } from "@/types/report"

const COLORS = [
  "#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#6366f1",
]

interface PeopleDashboardProps {
  data: EngagementPeopleRow[]
}

export function PeopleDashboard({ data }: PeopleDashboardProps) {
  const chartData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of data) {
      const type = row.workerType ?? "Unknown"
      counts.set(type, (counts.get(type) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [data])

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-500">
        No data available for dashboard.
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">People by Worker Type</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <XAxis
                dataKey="name"
                angle={-35}
                textAnchor="end"
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" name="People" radius={[4, 4, 0, 0]}>
                {chartData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  )
}
